import { Link } from 'react-router';
import type { TemplateDecisionSummaryDto } from '@pams/shared';
import { TEMPLATE_DECISION_CLASS_LABEL } from '@/lib/labels';
import { LIBRARY_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P7B §3e] DECISIONS OUTSTANDING, GROUPED — "N decisions affect M templates", then ONE ROW PER
 *  CLASS. Never a list of 182 conflicts: one decision reaches every template using its code, so the
 *  honest unit of work is the code, and the honest headline is how many templates are waiting on one.
 *
 *  The figures are the SERVER's (`templateSetSchema.decisions`) — the library header and the
 *  decisions page render the same summary, so the two screens cannot count differently.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const DecisionSummary = ({
  summary,
  setKey,
}: {
  summary: TemplateDecisionSummaryDto;
  setKey: string;
}): React.ReactElement => (
  <section
    className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm"
    data-testid="decision-summary"
    aria-label={LIBRARY_COPY.decisions}
  >
    <p className="font-medium text-heading" data-testid="decision-headline">
      {LIBRARY_COPY.decisionsHeadline(summary.outstanding, summary.templatesAffected)}
    </p>
    <ul className="flex flex-col gap-1">
      {summary.classes.map((row) => (
        <li
          key={row.class}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5"
          data-testid={`decision-class-${row.class}`}
        >
          <span className="min-w-40 text-foreground">{TEMPLATE_DECISION_CLASS_LABEL[row.class]}</span>
          <span className="text-muted-foreground">
            {LIBRARY_COPY.classRow(row.decided, row.outstanding, row.templatesAffected)}
          </span>
          {row.outstanding > 0 ? (
            <Link to={`/templates/${setKey}/decisions?class=${row.class}`} className="text-xs text-link">
              {LIBRARY_COPY.decideClass}
            </Link>
          ) : null}
        </li>
      ))}
    </ul>
  </section>
);
